import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  FileText, BarChart3, ImageOff, Tag, Loader2, Search, CheckCircle2, XCircle,
  AlertTriangle, ExternalLink, MousePointer2, Eye, TrendingUp, Globe,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import adminClient from '@/lib/api/adminClient';
import { formatDate } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────

interface BlogSeoItem {
  id: number;
  slug: string;
  title: string;
  summary_length: number;
  content_word_count: number;
  tags: string[];
  language: string;
  category: string | null;
  difficulty: string | null;
  has_cover_image: boolean;
  published_at: string | null;
  url: string;
}

interface KeywordRankResult {
  keyword: string;
  found: boolean;
  position: number | null;
  matched_url: string | null;
  snippet: string | null;
  results_scanned: number;
  error?: string;
}

interface AuditIssue {
  slug: string;
  issue_type: string;
  message: string;
  severity: string;
}

interface AuditResponse {
  audited_at: string;
  total_posts: number;
  posts: Array<{
    slug: string;
    title: string;
    title_ok: boolean;
    summary_ok: boolean;
    has_cover_image: boolean;
    has_tags: boolean;
    word_count_ok: boolean;
    rank_results: KeywordRankResult[] | null;
  }>;
  issues: AuditIssue[];
  summary: Record<string, number>;
}

interface GscRow {
  keys: string[];
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface GscResponse {
  site_url: string;
  start_date: string;
  end_date: string;
  dimensions: string[];
  rows: GscRow[];
  total_rows: number;
}

// ── Helpers ────────────────────────────────────────────────────

function get28dAgo(): string {
  const d = new Date();
  d.setDate(d.getDate() - 28);
  return d.toISOString().slice(0, 10);
}

function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Stat Card ────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, loading }: {
  label: string; value: string | number; icon: React.ElementType; color: string; loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            {loading ? (
              <div className="h-8 w-20 bg-muted rounded animate-pulse mt-1" />
            ) : (
              <p className="text-2xl font-bold mt-1">{value}</p>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Component ────────────────────────────────────────────────

export function SeoPage() {
  const [langFilter, setLangFilter] = useState('all');
  const queryOpts = { staleTime: 5 * 60 * 1000, retry: 1 };

  // ── GSC auto-load: top queries (past 28 days) ──
  const { data: gscQueries, isLoading: gscQueriesLoading } = useQuery<GscResponse>({
    queryKey: ['admin', 'gsc', 'queries'],
    queryFn: () => adminClient.get('/blog/seo/gsc', {
      params: { start_date: get28dAgo(), end_date: getToday(), dimensions: 'query', row_limit: 20 },
    }).then(r => r.data),
    ...queryOpts,
  });

  // ── GSC auto-load: top pages (past 28 days) ──
  const { data: gscPages, isLoading: gscPagesLoading } = useQuery<GscResponse>({
    queryKey: ['admin', 'gsc', 'pages'],
    queryFn: () => adminClient.get('/blog/seo/gsc', {
      params: { start_date: get28dAgo(), end_date: getToday(), dimensions: 'page', row_limit: 15 },
    }).then(r => r.data),
    ...queryOpts,
  });

  const gscConfigured = gscQueries !== undefined && !('error' in (gscQueries as any));

  // GSC KPI aggregation
  const gscKpis = useMemo(() => {
    const rows = gscQueries?.rows ?? [];
    const totalClicks = rows.reduce((s, r) => s + r.clicks, 0);
    const totalImpressions = rows.reduce((s, r) => s + r.impressions, 0);
    const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
    const avgPosition = rows.length > 0
      ? rows.reduce((s, r) => s + r.position * r.impressions, 0) / (totalImpressions || 1)
      : 0;
    return {
      clicks: totalClicks,
      impressions: totalImpressions,
      ctr: avgCtr.toFixed(2),
      position: avgPosition.toFixed(1),
    };
  }, [gscQueries]);

  // ── Blog overview ──
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['admin', 'seo-overview'],
    queryFn: () => adminClient.get('/blog/seo/overview').then((r) => r.data),
    ...queryOpts,
  });

  const posts: BlogSeoItem[] = overview?.posts ?? [];

  const filteredPosts = useMemo(
    () => langFilter === 'all' ? posts : posts.filter((p) => p.language === langFilter),
    [posts, langFilter],
  );

  const stats = useMemo(() => {
    const total = posts.length;
    const avgWords = total > 0 ? Math.round(posts.reduce((s, p) => s + p.content_word_count, 0) / total) : 0;
    const noCover = posts.filter((p) => !p.has_cover_image).length;
    const noTags = posts.filter((p) => p.tags.length === 0).length;
    return { total, avgWords, noCover, noTags };
  }, [posts]);

  // ── Rank Check ──
  const [rankKeywords, setRankKeywords] = useState('');
  const [rankDomain, setRankDomain] = useState('sentinel.redclawey.com');
  const rankMutation = useMutation({
    mutationFn: (payload: { keywords: string[]; domain: string; max_results: number }) =>
      adminClient.post('/blog/seo/rank-check', payload).then((r) => r.data),
  });

  const handleRankCheck = () => {
    const keywords = rankKeywords.split('\n').map((k) => k.trim()).filter(Boolean);
    if (keywords.length === 0) return;
    rankMutation.mutate({ keywords, domain: rankDomain, max_results: 30 });
  };

  // ── Audit ──
  const [auditLang, setAuditLang] = useState<string | undefined>(undefined);
  const [auditCheckRanks, setAuditCheckRanks] = useState(false);
  const auditMutation = useMutation({
    mutationFn: (payload: { lang?: string; check_ranks: boolean; max_results: number }) =>
      adminClient.post('/blog/seo/audit', payload).then((r) => r.data),
  });

  const handleAudit = () => {
    auditMutation.mutate({
      lang: auditLang === 'all' ? undefined : auditLang,
      check_ranks: auditCheckRanks,
      max_results: 20,
    });
  };

  // ── Render ──

  if (overviewLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">SEO 分析</h1>

      {/* ═══ GSC KPI Cards ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="GSC 總點擊"
          value={gscKpis.clicks.toLocaleString()}
          icon={MousePointer2}
          color="text-blue-500"
          loading={gscQueriesLoading}
        />
        <StatCard
          label="GSC 總曝光"
          value={gscKpis.impressions.toLocaleString()}
          icon={Eye}
          color="text-green-500"
          loading={gscQueriesLoading}
        />
        <StatCard
          label="平均 CTR"
          value={`${gscKpis.ctr}%`}
          icon={TrendingUp}
          color="text-amber-500"
          loading={gscQueriesLoading}
        />
        <StatCard
          label="平均排名"
          value={gscKpis.position}
          icon={Globe}
          color="text-purple-500"
          loading={gscQueriesLoading}
        />
      </div>

      {/* ═══ GSC Top Queries + Top Pages (side by side) ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Top Queries */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">熱門查詢字詞（28 天）</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {gscQueriesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : !gscQueries?.rows?.length ? (
              <div className="p-6 text-sm text-muted-foreground">
                GSC 尚未設定或無數據
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-2 text-muted-foreground font-medium">查詢字詞</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium">點擊</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium hidden sm:table-cell">曝光</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium hidden sm:table-cell">CTR</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium">排名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gscQueries.rows.slice(0, 15).map((row, i) => (
                      <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-2 font-medium text-xs max-w-[200px] truncate">{row.keys[0]}</td>
                        <td className="px-4 py-2 text-right font-bold text-brand-primary">{row.clicks}</td>
                        <td className="px-4 py-2 text-right hidden sm:table-cell">{row.impressions.toLocaleString()}</td>
                        <td className="px-4 py-2 text-right hidden sm:table-cell">{(row.ctr * 100).toFixed(1)}%</td>
                        <td className="px-4 py-2 text-right">
                          <span className={row.position <= 3 ? 'text-green-500 font-bold' : row.position <= 10 ? 'text-amber-500' : ''}>
                            {row.position.toFixed(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">熱門頁面（28 天）</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {gscPagesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : !gscPages?.rows?.length ? (
              <div className="p-6 text-sm text-muted-foreground">
                GSC 尚未設定或無數據
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-2 text-muted-foreground font-medium">頁面</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium">點擊</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium hidden sm:table-cell">曝光</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium">排名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gscPages.rows.slice(0, 10).map((row, i) => {
                      const path = row.keys[0].replace(/^https?:\/\/[^/]+/, '');
                      return (
                        <tr key={i} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-2 font-mono text-xs max-w-[250px] truncate" title={row.keys[0]}>
                            {path || '/'}
                          </td>
                          <td className="px-4 py-2 text-right font-bold text-brand-primary">{row.clicks}</td>
                          <td className="px-4 py-2 text-right hidden sm:table-cell">{row.impressions.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right">
                            <span className={row.position <= 3 ? 'text-green-500 font-bold' : row.position <= 10 ? 'text-amber-500' : ''}>
                              {row.position.toFixed(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ═══ Blog Health Summary ═══ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="文章總數" value={stats.total} icon={FileText} color="text-blue-500" />
        <StatCard label="平均字數" value={stats.avgWords.toLocaleString()} icon={BarChart3} color="text-green-500" />
        <StatCard label="缺少封面" value={stats.noCover} icon={ImageOff} color="text-orange-500" />
        <StatCard label="缺少標籤" value={stats.noTags} icon={Tag} color="text-red-500" />
      </div>

      {/* ═══ Action Tabs ═══ */}
      <Tabs defaultValue="rank-check">
        <TabsList>
          <TabsTrigger value="rank-check">排名檢查</TabsTrigger>
          <TabsTrigger value="audit">SEO 審計</TabsTrigger>
          <TabsTrigger value="posts">文章列表</TabsTrigger>
        </TabsList>

        {/* ── Tab: Rank Check ── */}
        <TabsContent value="rank-check">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">DuckDuckGo 排名檢查</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">關鍵字（每行一個）</label>
                  <textarea
                    className="w-full h-32 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={"量化交易\ntrading bot\n加密貨幣策略"}
                    value={rankKeywords}
                    onChange={(e) => setRankKeywords(e.target.value)}
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">目標域名</label>
                    <Input value={rankDomain} onChange={(e) => setRankDomain(e.target.value)} />
                  </div>
                  <Button
                    onClick={handleRankCheck}
                    disabled={rankMutation.isPending || !rankKeywords.trim()}
                  >
                    {rankMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />檢查中...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" />檢查排名</>
                    )}
                  </Button>
                  {rankMutation.isPending && (
                    <p className="text-xs text-muted-foreground">
                      每個關鍵字間隔 2 秒，請耐心等待...
                    </p>
                  )}
                </div>
              </div>

              {rankMutation.data && (
                <div className="mt-4">
                  <p className="text-sm mb-2">
                    找到 <strong>{rankMutation.data.keywords_found}</strong> / {rankMutation.data.keywords_total} 個關鍵字
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>關鍵字</TableHead>
                        <TableHead>排名</TableHead>
                        <TableHead>匹配網址</TableHead>
                        <TableHead>摘要</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(rankMutation.data.results as KeywordRankResult[]).map((r) => (
                        <TableRow key={r.keyword}>
                          <TableCell className="font-medium">{r.keyword}</TableCell>
                          <TableCell>
                            {r.found ? (
                              <Badge variant="success">#{r.position}</Badge>
                            ) : (
                              <Badge variant="destructive">未找到</Badge>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[300px]">
                            {r.matched_url ? (
                              <a href={r.matched_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs hover:underline truncate block">
                                {r.matched_url}
                              </a>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="max-w-[300px] text-xs text-muted-foreground truncate">
                            {r.snippet || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: SEO Audit ── */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">SEO 審計</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <Select value={auditLang ?? 'all'} onValueChange={(v) => setAuditLang(v === 'all' ? undefined : v)}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="語言" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部語言</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="zh-TW">繁體中文</SelectItem>
                    <SelectItem value="zh-CN">简体中文</SelectItem>
                    <SelectItem value="ko">한국어</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-2">
                  <Switch checked={auditCheckRanks} onCheckedChange={setAuditCheckRanks} id="check-ranks" />
                  <label htmlFor="check-ranks" className="text-sm">包含排名檢查</label>
                </div>

                <Button onClick={handleAudit} disabled={auditMutation.isPending}>
                  {auditMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />審計中...</>
                  ) : '執行審計'}
                </Button>
              </div>

              {auditCheckRanks && (
                <p className="text-xs text-orange-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />包含排名檢查時，審計可能需要數分鐘
                </p>
              )}

              {auditMutation.data && <AuditResults data={auditMutation.data as AuditResponse} />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Posts List ── */}
        <TabsContent value="posts">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">文章 SEO 狀態</CardTitle>
              <Select value={langFilter} onValueChange={setLangFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部語言</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="zh-TW">繁體中文</SelectItem>
                  <SelectItem value="zh-CN">简体中文</SelectItem>
                  <SelectItem value="ko">한국어</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>標題</TableHead>
                      <TableHead>語言</TableHead>
                      <TableHead>字數</TableHead>
                      <TableHead>摘要長度</TableHead>
                      <TableHead>封面</TableHead>
                      <TableHead>標籤</TableHead>
                      <TableHead>發佈日期</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPosts.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="max-w-[300px]">
                          <a href={p.url} target="_blank" rel="noopener noreferrer"
                            className="text-sm hover:underline flex items-center gap-1">
                            <span className="truncate">{p.title}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
                          </a>
                        </TableCell>
                        <TableCell><Badge variant="outline">{p.language}</Badge></TableCell>
                        <TableCell>
                          <span className={p.content_word_count < 1000 ? 'text-orange-500' : ''}>
                            {p.content_word_count.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            p.summary_length >= 120 && p.summary_length <= 160 ? 'success'
                              : p.summary_length < 50 ? 'destructive' : 'warning'
                          }>{p.summary_length}</Badge>
                        </TableCell>
                        <TableCell>
                          {p.has_cover_image
                            ? <CheckCircle2 className="w-4 h-4 text-green-500" />
                            : <XCircle className="w-4 h-4 text-red-500" />}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {p.tags.slice(0, 3).map((t) => (
                              <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                            ))}
                            {p.tags.length > 3 && <Badge variant="outline" className="text-xs">+{p.tags.length - 3}</Badge>}
                            {p.tags.length === 0 && <span className="text-xs text-red-500">None</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.published_at ? formatDate(p.published_at) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                顯示 {filteredPosts.length} / {posts.length} 篇文章
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Audit Results Sub-component ──────────────────────────────

function AuditResults({ data }: { data: AuditResponse }) {
  const { summary, issues, total_posts } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg bg-muted">
          <p className="text-xs text-muted-foreground">審計文章數</p>
          <p className="text-xl font-bold">{total_posts}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted">
          <p className="text-xs text-muted-foreground">問題總數</p>
          <p className="text-xl font-bold text-orange-500">{summary.total_issues}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted">
          <p className="text-xs text-muted-foreground">缺少封面</p>
          <p className="text-xl font-bold">{summary.no_cover_image ?? 0}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted">
          <p className="text-xs text-muted-foreground">字數不足</p>
          <p className="text-xl font-bold">{summary.low_word_count ?? 0}</p>
        </div>
      </div>

      {issues.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Slug</TableHead>
              <TableHead>問題類型</TableHead>
              <TableHead>說明</TableHead>
              <TableHead>嚴重程度</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {issues.map((issue, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm font-mono">{issue.slug}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{issue.issue_type}</Badge></TableCell>
                <TableCell className="text-sm">{issue.message}</TableCell>
                <TableCell>
                  <Badge variant={issue.severity === 'error' ? 'destructive' : 'warning'}>{issue.severity}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p className="text-sm text-green-500 flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4" /> 所有文章通過 SEO 審計
        </p>
      )}
    </div>
  );
}
